import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import YouTube from 'youtube-iframe';
import Player from '@vimeo/player';

gsap.registerPlugin(ScrollTrigger);

export default class VideoHandler {
	customPlayer() {
		gsap.utils.toArray('[data-video="container"]').forEach((e) => {
			if (!e) return;

			const videoType = e.getAttribute("data-video-type");
			
			const video = e.querySelector('[data-video-url]');
			if (!video) return;

			const url = video.getAttribute("data-video-url");
			if (!url) return;
			
			const playButton = e.querySelector('[data-video-play]');
			const muteButton = e.querySelector('[data-video-mute]');
			const captionsButton = e.querySelector('[data-video-captions]');
			const overlayButton = e.querySelector('[data-video-overlay] button');
			const overlay = e.querySelector('[data-video-overlay]');
			const controls = e.querySelector('[data-video-controls]');
			const playType = e.getAttribute("data-video-play");
			const { type, id, hash } = getVideoTypeAndId(url);
			let player;
			// Subtitles default to on for YouTube only
			let captionsOn = type === "youtube";
			if (captionsButton) {
				captionsButton.setAttribute('aria-pressed', captionsOn ? 'true' : 'false');
				const captionsIcon = captionsButton.querySelector('[data-video-captions-icon]');
				if (captionsIcon) captionsIcon.classList.toggle('opacity-40', !captionsOn);
			}

			if (type === "youtube") {
				loadYouTubePlayer(id);
			} else if (type === "vimeo") {
				loadVimeoPlayer(id, hash, url);
			} else if (type === "mp4") {
				loadMP4Video(url);
			}


			function getVideoTypeAndId(url) {
				const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]+)/;
				const vimeoRegex = /(?:vimeo\.com\/(?:video\/)?(?:channels\/\w+\/)?(?:groups\/\w+\/videos\/)?(?:album\/\w+\/video\/)?(?:showcase\/\w+\/video\/)?)([\d]+)(?:\/([a-zA-Z0-9]+))?/;
				const mp4Regex = /\.mp4(\?.*)?$/;

				let match;
				if ((match = url.match(youtubeRegex))) {
					return { type: "youtube", id: match[1] };
				} else if ((match = url.match(vimeoRegex))) {
					// For Vimeo, combine video ID and privacy hash if present
					const videoId = match[1];
					const privacyHash = match[2]; // This captures the hash like 'b50ca94c5c'
					return { 
						type: "vimeo", 
						id: videoId,
						hash: privacyHash || null
					};
				} else if (mp4Regex.test(url)) {
					return { type: "mp4", id: url };
				}
				return { type: "mp4", id: url };
			}

			function loadYouTubePlayer(videoId) {
				require('youtube-iframe').load(function(YT) {
					player = new YT.Player(video, {
						videoId: videoId,
						playerVars: {
							controls: 0,
							modestbranding: 1,
							rel: 0,
							showinfo: 0,
							iv_load_policy: 3,
							cc_load_policy: captionsOn ? 1 : 0,
							fs: 0,
							autohide: 1,
							playsinline: 1,
							disablekb: 1
						},
						events: {
							onReady: function (event) {
								setCaptions(event.target, captionsOn);
								event.target.mute();
								// Hide YouTube's play button overlay
								const iframe = event.target.getIframe();
								try {
									const style = document.createElement('style');
									style.textContent = '.ytp-large-play-button, .ytp-cued-thumbnail-overlay { display: none !important; }';
									iframe.contentDocument.head.appendChild(style);
								} catch (e) {
									// Cross-origin restriction - can't access iframe content
									console.log('Cannot hide YouTube controls due to CORS');
								}

								if (playType === "autoplay") {
									event.target.mute();
									setupScrollTrigger(event.target);
								} else {
									if (playButton) {
										setupPlayButton(event.target);
									}
								}

							},
							onStateChange: function(event) {
								// YouTube can re-enable captions (user prefs/video defaults) on playback
								if (event.data === YT.PlayerState.PLAYING && !captionsOn) {
									setCaptions(event.target, false);
								}
								if (event.data === YT.PlayerState.PLAYING) {
									const iframe = event.target.getIframe();
									if (iframe.classList.contains('pointer-events-none')) {
										iframe.classList.remove('pointer-events-none');
									}
								}
							}
						},
					});
				});
			}

			function loadVimeoPlayer(videoId, privacyHash, originalUrl) {
				const playerOptions = {
					controls: false,
					muted: true,
					loop: true,
				};
				
				// For private/unlisted videos with hash, use the full URL
				// Otherwise use the video ID
				if (privacyHash) {
					playerOptions.url = originalUrl;
				} else {
					playerOptions.id = videoId;
				}
				
				player = new Player(video, playerOptions);

				player.ready().then(() => {
					setCaptions(player, false);
					if (playType === "autoplay") {
						setupScrollTrigger(player);
					} else {
						if (playButton) {
							setupPlayButton(player);
						}
					}
				});
			}
			
			function loadMP4Video(videoUrl) {
				const videoElement = document.createElement("video");
				videoElement.src = videoUrl;
				videoElement.controls = false;
				videoElement.classList.add('w-full');
				videoElement.classList.add('h-full');
				videoElement.classList.add('object-cover');
				videoElement.muted = true;
				videoElement.playsInline = true;
				videoElement.loop = true;

				video.innerHTML = "";
				video.appendChild(videoElement);

				if (playType === "autoplay") {
					setupScrollTrigger(videoElement);
				} else {
					if (playButton) {
						setupPlayButton(videoElement);
					}
				}
			}

			function setupScrollTrigger(videoPlayer) {
				ScrollTrigger.create({
					trigger: e,
					start: "top 100%",
					end: "bottom 0%",
					toggleActions: "play pause resume pause",
					onEnter: () => playVideo(videoPlayer),
				});
				if (e.querySelector('[data-video-poster]')) {
					e.querySelector('[data-video-poster]').classList.add('hidden')
				};
			}
			
			function setupPlayButton(videoPlayer) {
				const playIconSpan = playButton ? playButton.querySelector('span:first-child') : null;
				const muteIconSpan = muteButton ? muteButton.querySelector('span:first-child') : null;

				// Get theme URI from localized script
				const themeUri = typeof themeData !== 'undefined' ? themeData.theme_uri : '';

				// Load SVG icons
				const playSvg = fetch(`${themeUri}/_dist/images/ui/play.svg`)
					.then(res => res.text())
					.catch(() => 'Play');

				const pauseSvg = fetch(`${themeUri}/_dist/images/ui/pause.svg`)
					.then(res => res.text())
					.catch(() => 'Pause');

				const muteSvg = fetch(`${themeUri}/_dist/images/ui/mute.svg`)
					.then(res => res.text())
					.catch(() => 'Mute');

				const unmuteSvg = fetch(`${themeUri}/_dist/images/ui/unmute.svg`)
					.then(res => res.text())
					.catch(() => 'Unmute');

				// Helper functions for overlay and controls
				function hideOverlay() {
					if (overlay) {
						overlay.classList.add('opacity-0', 'invisible');
						overlay.classList.remove('opacity-100');
					}
				}

				function showControls() {
					if (controls) {
						controls.classList.remove('opacity-0', 'invisible');
						controls.classList.add('opacity-100', 'visible');
					}
				}

				function hideControls() {
					if (controls) {
						controls.classList.add('opacity-0', 'invisible');
						controls.classList.remove('opacity-100', 'visible');
					}
				}

				// Overlay play button handler
				if (overlayButton) {
					overlayButton.addEventListener("click", async () => {
						playVideo(videoPlayer);
						hideOverlay();
						showControls();
						if (e.querySelector('[data-video-poster]')) {
							e.querySelector('[data-video-poster]').classList.add('hidden');
						}
						if (playIconSpan) playIconSpan.innerHTML = await pauseSvg;
						// Update mute button icon to reflect current mute state
						if (muteIconSpan) {
							const muted = await isMuted(videoPlayer);
							muteIconSpan.innerHTML = muted ? await muteSvg : await unmuteSvg;
						}
					});
				}

				// Play/Pause button in controls
				if (playButton) {
					playButton.addEventListener("click", async () => {
						if (await isPlaying(videoPlayer)) {
							pauseVideo(videoPlayer);
							if (playIconSpan) playIconSpan.innerHTML = await playSvg;
						} else {
							playVideo(videoPlayer);
							if (playIconSpan) playIconSpan.innerHTML = await pauseSvg;
							if (e.querySelector('[data-video-poster]')) {
								e.querySelector('[data-video-poster]').classList.add('hidden');
							}
							// Update mute button icon to reflect current mute state
							if (muteIconSpan) {
								const muted = await isMuted(videoPlayer);
								muteIconSpan.innerHTML = muted ? await muteSvg : await unmuteSvg;
							}
						}
					});
				}

				// Subtitles toggle
				if (captionsButton) {
					captionsButton.addEventListener("click", async () => {
						captionsOn = !captionsOn;
						const ok = await setCaptions(videoPlayer, captionsOn);
						if (ok === false) captionsOn = !captionsOn;
						captionsButton.setAttribute('aria-pressed', captionsOn ? 'true' : 'false');
						const icon = captionsButton.querySelector('[data-video-captions-icon]');
						if (icon) icon.classList.toggle('opacity-40', !captionsOn);
					});
				}

				// Mute/Unmute button
				if (muteButton) {
					muteButton.addEventListener("click", async () => {
						if (await isMuted(videoPlayer)) {
							unmuteVideo(videoPlayer);
							if (muteIconSpan) muteIconSpan.innerHTML = await unmuteSvg;
						} else {
							muteVideo(videoPlayer);
							if (muteIconSpan) muteIconSpan.innerHTML = await muteSvg;
						}
					});
				}
			}

			async function setCaptions(videoPlayer, enable) {
				try {
					if (type === "youtube") {
						if (enable) {
							videoPlayer.loadModule('captions');
						} else {
							videoPlayer.unloadModule('captions');
						}
					} else if (type === "vimeo") {
						if (enable) {
							const tracks = await videoPlayer.getTextTracks();
							if (!tracks.length) return false;
							await videoPlayer.enableTextTrack(tracks[0].language, tracks[0].kind);
						} else {
							await videoPlayer.disableTextTrack();
						}
					} else if (videoPlayer.textTracks) {
						if (!videoPlayer.textTracks.length) return false;
						for (const track of videoPlayer.textTracks) {
							track.mode = enable ? 'showing' : 'hidden';
						}
					}
				} catch (err) {
					return false;
				}
				return true;
			}

			function playVideo(videoPlayer) {
				if (videoPlayer.play) {
					if (type === "mp4") {
						videoPlayer.load();
					}
					videoPlayer.play();
				} else if (videoPlayer.playVideo) {
					videoPlayer.playVideo();
				}
			}

			function pauseVideo(videoPlayer) {
				if (videoPlayer.pause) {
					videoPlayer.pause();
				} else if (videoPlayer.pauseVideo) {
					videoPlayer.pauseVideo();
				}
			}
			
			async function isPlaying(videoPlayer) {
				if (videoPlayer.getPlayerState) {
					return videoPlayer.getPlayerState() === 1;
				} else if (videoPlayer.getPaused) {
					try {
						const isPaused = await videoPlayer.getPaused();
						return !isPaused;
					} catch (e) {
						return false;
					}
				} else {
					return !videoPlayer.paused;
				}
			}
			
			function muteVideo(videoPlayer) {
				if (videoPlayer.mute) {
					videoPlayer.mute();
				} else if (videoPlayer.setVolume) {
					videoPlayer.setVolume(0);
				} else {
					videoPlayer.muted = true;
				}
			}
			
			function unmuteVideo(videoPlayer) {
				if (videoPlayer.unMute) {
					videoPlayer.unMute();
				} else if (videoPlayer.setVolume) {
					videoPlayer.setVolume(1);
				} else {
					videoPlayer.muted = false;
				}
			}
			
			async function isMuted(videoPlayer) {
				if (videoPlayer.isMuted) {
					return videoPlayer.isMuted();
				} else if (videoPlayer.getVolume) {
					try {
						const volume = await videoPlayer.getVolume();
						return volume === 0;
					} catch (e) {
						return true;
					}
				} else {
					return videoPlayer.muted;
				}
			}

		});
	}
}